// ============================================================================
// NARRATIVE PREVIEW (FSA-1)
// ============================================================================
// Renders a NARRATIVE-type report (Significant Accounting Policies,
// Auditor's Report, Director's Report, CARO) as the formatted body
// document with placeholder tokens highlighted.

import React, { useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type {
  TemplateReportNode,
  NarrativePlaceholder,
} from 'api/types/fsa/template.types';
import { parsePlaceholders } from 'api/types/fsa/template.types';

interface NarrativePreviewProps {
  reportNode: TemplateReportNode;
}

const NarrativePreview: React.FC<NarrativePreviewProps> = ({ reportNode }) => {
  const { report, narrative } = reportNode;

  const placeholders: NarrativePlaceholder[] = useMemo(
    () => (narrative ? parsePlaceholders(narrative) : []),
    [narrative]
  );

  return (
    <div style={{ padding: '0.5rem 0' }}>
      <header style={{ marginBottom: '0.75rem' }}>
        <div style={subtitleStyle}>
          NARRATIVE TEMPLATE
          {narrative?.narrativeCode && (
            <span style={{ marginLeft: 8, fontFamily: 'monospace', textTransform: 'none', fontWeight: 600 }}>
              {narrative.narrativeCode}{narrative.narrativeVersion != null ? ` · v${narrative.narrativeVersion} (latest installed)` : ''}
            </span>
          )}
        </div>
        <h2 style={titleStyle}>{report.name}</h2>
        {report.description && <div style={descStyle}>{report.description}</div>}
        <div style={legendStyle}>
          <IconSystem.info size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
          This is a system-seeded skeleton. Review and edit the body before issuing — text below
          is illustrative only.
        </div>
      </header>

      {/* Body */}
      <div style={paperStyle}>
        {narrative && narrative.bodyHtml ? (
          <div
            style={bodyStyle}
            dangerouslySetInnerHTML={{ __html: narrative.bodyHtml }}
          />
        ) : (
          <div style={emptyStyle}>
            No narrative body authored yet.
          </div>
        )}
      </div>

      {/* Placeholders palette */}
      {placeholders.length > 0 && (
        <section style={{ marginTop: '1rem' }}>
          <h3 style={sectionTitleStyle}>Available Placeholders</h3>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: 0, marginBottom: 8 }}>
            These tokens are auto-substituted with engagement data when the document is generated.
          </p>
          <div style={placeholderListStyle}>
            {placeholders.map((p) => (
              <div key={p.token} style={placeholderItemStyle}>
                <code style={tokenStyle}>{p.token}</code>
                <span style={placeholderLabelStyle}>{p.label}</span>
                {p.required && <span style={requiredChipStyle}>required</span>}
                {p.source && <span style={sourceChipStyle}>{p.source}</span>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default NarrativePreview;

// ============================================================================
// STYLES
// ============================================================================

const subtitleStyle: React.CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: 1,
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '1.05rem',
  fontWeight: 700,
  color: 'var(--text-primary)',
};

const descStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--text-secondary)',
  marginTop: 4,
  lineHeight: 1.5,
};

const legendStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
  marginTop: 6,
  padding: '4px 8px',
  background: '#fef3c7',
  borderLeft: '3px solid #f59e0b',
  display: 'inline-block',
};

const paperStyle: React.CSSProperties = {
  border: '1px solid var(--border-color)',
  borderRadius: 6,
  padding: '1rem 1.25rem',
  background: 'var(--bg-primary)',
};

const bodyStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  color: 'var(--text-primary)',
  lineHeight: 1.6,
};

const emptyStyle: React.CSSProperties = {
  fontSize: '0.8125rem',
  color: 'var(--text-secondary)',
  fontStyle: 'italic',
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  color: 'var(--text-secondary)',
  margin: '0 0 0.25rem 0',
  paddingBottom: '0.25rem',
  borderBottom: '1px solid var(--border-color)',
};

const placeholderListStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const placeholderItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.4rem 0.5rem',
  border: '1px solid var(--border-color)',
  borderRadius: 4,
  background: 'var(--bg-secondary)',
  fontSize: '0.75rem',
};

const tokenStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontWeight: 600,
  color: '#1e3a8a',
  background: '#dbeafe',
  padding: '1px 6px',
  borderRadius: 3,
};

const placeholderLabelStyle: React.CSSProperties = {
  flex: 1,
  color: 'var(--text-primary)',
};

const requiredChipStyle: React.CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 600,
  padding: '1px 6px',
  borderRadius: 999,
  background: '#fee2e2',
  color: '#991b1b',
};

const sourceChipStyle: React.CSSProperties = {
  fontSize: '0.65rem',
  padding: '1px 6px',
  borderRadius: 999,
  background: 'var(--bg-primary)',
  color: 'var(--text-secondary)',
  border: '1px solid var(--border-color)',
};
